import { IAssetFetcher, IAssetMap, IImageAsset, IImageAssetMap } from '../interface/asset';
import { default as JSON_IMAGE } from './asset.image.json';

const DATA_IMAGE: IImageAssetMap = JSON_IMAGE;

const DATA_ALL: IAssetMap = {
    Image: DATA_IMAGE,
};

/** the maths checks out - Alex */
function _weightings<T>(
    valuesIn: Array<T>,
    targetValue: number,
    weightGenerator: (valIn: T) => number,
): {
    minValue: number;
    maxValue: number;
    weightings: Array<number>;
    weightingsNormalized: Array<number>;
} {
    const weightingsAll = valuesIn.map(val => weightGenerator(val));
    const weightingsMin = Math.min(...weightingsAll);
    const weightingsMax = Math.max(...weightingsAll);
    const weightingsDelta = weightingsMax - weightingsMin;
    /* Take all of the weight values and place it within the range of possible values, 
       such that weightingsNormalized contains values between 0->1 only, where 0=targetValue & 1=further value away received */
    const weightingsNormalized = weightingsAll.map(
        weight => Math.abs(weight - targetValue) / weightingsDelta,
    );

    return {
        minValue: weightingsMin,
        maxValue: weightingsMax,
        weightings: weightingsAll,
        weightingsNormalized: weightingsNormalized,
    };
}

function _closestImage(
    target: { width: number; height: number },
    allImages: Array<IImageAsset>,
): IImageAsset {
    /* for each image generate a weighting value, where 0=same as target & 1=furthest value away */
    const weightingsAR = _weightings(
        allImages,
        target.width / target.height,
        image => image.width / image.height,
    );
    const weightingsWidth = _weightings(allImages, target.width, image => image.width);
    const weightingsHeight = _weightings(allImages, target.height, image => image.height);

    const weightedImages = allImages.map((image, idx) => ({
        ...image,
        weight:
            /* take the weightings generated above & generate an overal weight value, 
                where the aspect-ratio takes priority over image size */
            weightingsAR.weightingsNormalized[idx] * 0.5 +
            weightingsWidth.weightingsNormalized[idx] * 0.25 +
            weightingsHeight.weightingsNormalized[idx] * 0.25,
    }));

    /* sort by smallest weight first */
    weightedImages.sort((prev, next) => prev.weight - next.weight);

    /* & pick it out, this image is the closest to #target above */
    const asset = weightedImages.shift() ?? allImages[0];

    return asset;
}

function _imageAsset<K extends keyof IImageAssetMap, V extends keyof IImageAssetMap[K]>(
    group: K,
    name: V,
): IImageAssetMap[K][V] {
    return DATA_IMAGE[group][name];
}

function _imageAssetGroup<K extends keyof IImageAssetMap>(group: K): Array<IImageAsset> {
    return Object.values(DATA_IMAGE[group]);
}

function _imageAssetGroupClosestMatch<K extends keyof IImageAssetMap>(
    group: K,
    targetWidth: number,
    targetHeight: number,
): IImageAsset {
    const allValues = Object.values(DATA_IMAGE[group]);
    const image = _closestImage({ width: targetWidth, height: targetHeight }, allValues);
    return image;
}

function AssetFetcher(): IAssetFetcher {
    return {
        imageAsset: _imageAsset,
        imageAssetGroup: _imageAssetGroup,
        imageAssetGroupClosestMatch: _imageAssetGroupClosestMatch,
    };
}

export default AssetFetcher;
